import { FranchisePartner } from '../hooks/useFranchisePartnersData'
import { formatToLongDate } from '../../../../utils/textUtils'
import { generateInspectorUrl } from '../../../../utils/urls'
import { getResource } from '../../../../types/resources'

// Utility function to handle UTC datetime strings from backend
export const parseUTCDateTime = (dateString: string): Date => {
  // Backend sends UTC datetime strings without timezone info
  // We need to explicitly treat them as UTC to avoid timezone issues
  const utcDateString = dateString.includes('Z') || dateString.includes('+')
    ? dateString
    : dateString + 'Z' // Append 'Z' to indicate UTC if not already present

  return new Date(utcDateString)
}

export const formatters = {
  // Parse UTC datetime string to Date object
  parseUTCDateTime,

  // Get last sync date from franchise partner
  getLastSyncDate: (fp: FranchisePartner): string => {
    // Use real last_sync_at data from the backend API
    if (!fp.last_sync_at) {
      return 'Never synced'
    }

    try {
      // Format using the formatting function
      const formattedDate = formatToLongDate(fp.last_sync_at)

      // Add sync system info if available
      const systemInfo = fp.last_sync_system ? ` (${fp.last_sync_system})` : ''

      return `${formattedDate}${systemInfo}`
    } catch (error) {
      console.warn('Failed to parse last sync date:', fp.last_sync_at, error)
      return 'Invalid date'
    }
  },

  // Get connected systems list
  getConnectedSystems: (fp: FranchisePartner): string[] => {
    const systems: string[] = []
    if (fp.has_complete_lightspeed_connection) systems.push('lightspeed')
    if (fp.has_complete_xero_connection) systems.push('xero')
    // Note: Skimmer is intentionally excluded even if connected - not live yet
    // if (fp.has_complete_skimmer_connection) systems.push('skimmer')
    return systems
  },

  // Get system connection status
  getSystemStatus: (fp: FranchisePartner, system: string): 'connected' | 'incomplete' | 'not_configured' => {
    switch (system) {
      case 'lightspeed':
        if (!fp.lightspeed_store_code) return 'not_configured'
        return fp.has_complete_lightspeed_connection ? 'connected' : 'incomplete'
      case 'xero':
        if (!fp.xero_short_code) return 'not_configured'
        return fp.has_complete_xero_connection ? 'connected' : 'incomplete'
      case 'skimmer':
        // Skimmer logic here if needed
        return 'not_configured'
      default:
        return 'not_configured'
    }
  },

  // Format data count for display
  formatDataCount: (count: number | undefined): string => {
    if (count === undefined || count === null) return '-'
    if (count === 0) return '0'
    return count.toLocaleString()
  },

  // Get total data count
  getTotalDataCount: (fp: FranchisePartner): number => {
    return fp.total_data_count ||
      ((fp.products_count || 0) + (fp.sales_count || 0) + (fp.clients_count || 0) +
        (fp.xero_invoices_count || 0) + (fp.xero_payments_count || 0))
  },

  // Get inspector URL (tenure-pinned by the FP's DB id)
  getInspectorUrl: (fp: FranchisePartner): string => {
    return generateInspectorUrl(fp.fp_code, { id: fp.id })
  },

  // Get external system URLs
  getLightspeedUrl: (fp: FranchisePartner): string | null => {
    return fp.lightspeed_store_code
      ? `https://${fp.lightspeed_store_code}.retail.lightspeed.app/`
      : null
  },

  getXeroUrl: (fp: FranchisePartner): string | null => {
    return fp.xero_short_code
      ? `https://my.xero.com/!xkcD/Action/OrganisationLogin/${fp.xero_short_code}`
      : null
  },

  getPooltrackrUrl: (country: string): string => {
    return country === 'NZ'
      ? 'https://poolwerxnz.pooltrackr.com/'
      : 'https://poolwerx.pooltrackr.com/'
  },

  getSkimmerUrl: (): string => {
    return 'https://getskimmer.com/App/Quickstart/'
  },

  getGoogleDriveUrl: (fp: FranchisePartner): string | null => {
    return getResource(fp.resources, 'shared_drive')?.url ?? null
  }
}

export const validators = {
  // Check if sync can be triggered for a system
  canTriggerSync: (fp: FranchisePartner, system: string): boolean => {
    switch (system) {
      case 'lightspeed':
        return !!fp.has_complete_lightspeed_connection
      case 'xero':
        return !!fp.has_complete_xero_connection
      case 'skimmer':
        return !!fp.has_complete_skimmer_connection
      case 'all':
        return !!fp.has_complete_lightspeed_connection ||
               !!fp.has_complete_xero_connection
        // Skimmer excluded from 'all' - not live yet
      default:
        return false
    }
  },

  // Check if data can be cleared for given systems
  canClearData: (fp: FranchisePartner, systems: string[]): boolean => {
    if (systems.length === 0) return false

    return systems.every(system => {
      switch (system) {
        case 'lightspeed':
          return !!fp.has_complete_lightspeed_connection
        case 'xero':
          return !!fp.has_complete_xero_connection
        case 'skimmer':
          // Skimmer clearing not live yet
          return false
        default:
          return false
      }
    })
  },

  // Check if system is connected
  isSystemConnected: (fp: FranchisePartner, system: string): boolean => {
    switch (system) {
      case 'lightspeed':
        return !!fp.has_complete_lightspeed_connection
      case 'xero':
        return !!fp.has_complete_xero_connection
      case 'skimmer':
        return !!fp.has_complete_skimmer_connection
      default:
        return false
    }
  },

  // Check if system is configured (has credentials)
  isSystemConfigured: (fp: FranchisePartner, system: string): boolean => {
    switch (system) {
      case 'lightspeed':
        return !!fp.lightspeed_store_code
      case 'xero':
        return !!fp.xero_short_code
      case 'pooltrackr':
        return fp.field_management_system === 'Pooltrackr'
      case 'skimmer':
        return fp.field_management_system === 'Skimmer'
      default:
        return false
    }
  },

  // Validate FP code
  isValidFpCode: (fpCode: string | undefined | null): boolean => {
    return !!fpCode &&
           fpCode.toLowerCase() !== 'undefined' &&
           fpCode.toLowerCase() !== 'null'
  }
}